import { createForm } from "@tanstack/solid-form";
import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { Copy, Info, Plus, Trash } from "lucide-solid";
import type { Component } from "solid-js";
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import { Portal } from "solid-js/web";
import DeleteUserIcon from "~/Icons/Icon_24_Delete_User_Male.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import {
	Button,
	formatNumberWithSpaces,
	getPublicMessageFromError,
	Input,
	InputLabelError,
	ModalV2Ui,
	StyledTableNew,
	validateEmailString
} from "~/solidJs/shared";
import {
	type AddMembersRequest,
	MemberRole,
	TeamApi,
	type TeamMember
} from "~/solidJs/shared/api/TeamApi";
import { TeamInvitationPageParams } from "~/solidJs/shared/models/pageParams";
import { InputLabel } from "~/solidJs/shared/ui";
import { StyledTooltip } from "~/solidJs/shared/ui/atoms/Tooltip";
import {
	PlanStatus,
	StatusBadge
} from "~/solidJs/widgets/SubscriptionInfoWidget/SubscriptionWidget";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import { getUserStore } from "../../HeaderMain/models";
import * as TeamMethods from "../methods";
import { openContactUsForm } from "../utils/contactUs";
import { SeatsNumberInput } from "./SeatsNumberInput";
import { TeamSeatsModal } from "./TeamSeatsModal";

// Утилита для получения инициалов из имени
const getInitials = (name: string | null): string => {
	return name?.[0] || "";
};

const EmptyMemberSlot: Component<{
	onClick: () => void;
	usedCredits: number;
	totalCredits: number;
	willBeDeleted: string | null;
	hideCredits?: boolean;
}> = (props) => {
	const willBeDeletedDate = createMemo(() => {
		if (!props.willBeDeleted) return null;
		const date = new Date(props.willBeDeleted);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric"
		});
	});

	return (
		<StyledTableNew.TableRow class="group">
			<StyledTableNew.TableCell class="!py-[11px] !bg-white !rounded-l-2xl border border-border-gray-dark border-r-0 border-dashed transition-colors group-hover:border-interaction-violet">
				<div class="flex h-8 items-center gap-1.5">
					<div
						class="flex cursor-pointer items-center gap-1.5"
						onClick={() => {
							EventAnalytics.team.inviteMember.inviteClick();
							EventAnalytics.team.management.inviteMemberClicked();
							props.onClick();
						}}
					>
						<div class="flex h-4 w-4 items-center justify-center text-interaction-violet transition-colors group-hover:text-interaction-violet-hv">
							<Plus size={16} />
						</div>
						<span class="caption-12-medium text-interaction-violet transition-colors group-hover:text-interaction-violet-hv">
							Invite member
						</span>
					</div>
					<Show when={props.willBeDeleted}>
						<StyledTooltip.TooltipClosedComponent
							content={
								<div class="flex max-w-[320px] flex-col gap-1 p-4">
									<span class="caption-12-semibold text-white">
										This seat will be removed
									</span>
									<span class="caption-12-regular text-white">
										This member will be removed from your team on{" "}
										{willBeDeletedDate()}
									</span>
								</div>
							}
							rootProps={{
								positioning: {
									placement: "top"
								}
							}}
						>
							{(buttonProps) => (
								<button {...buttonProps} type="button" class="flex items-center">
									<Info size={16} class="shrink-0 text-text-secondary-v2" />
								</button>
							)}
						</StyledTooltip.TooltipClosedComponent>
					</Show>
				</div>
			</StyledTableNew.TableCell>
			<StyledTableNew.TableCell class="!py-[11px] !bg-white !rounded-none border border-border-gray-dark border-x-0 border-dashed transition-colors group-hover:border-interaction-violet">
				<div class="flex h-8 items-center">
					<span class="caption-12-medium text-text-tertiary">Member</span>
				</div>
			</StyledTableNew.TableCell>
			<StyledTableNew.TableCell
				class={`!py-[11px] !bg-white border border-border-gray-dark border-dashed text-center transition-colors group-hover:border-interaction-violet ${props.hideCredits ? "!rounded-r-2xl border-l-0" : "!rounded-none border-x-0"}`}
			>
				<div class="flex h-8 items-center justify-center">
					<span class="caption-12-medium text-text-tertiary">Teams</span>
				</div>
			</StyledTableNew.TableCell>
			<Show when={!props.hideCredits}>
				<StyledTableNew.TableCell class="!py-[11px] !bg-white !rounded-r-2xl border border-border-gray-dark border-l-0 border-dashed text-right transition-colors group-hover:border-interaction-violet">
					<div class="flex h-8 items-center justify-end">
						<span class="caption-12-medium text-text-tertiary">
							{formatNumberWithSpaces(props.usedCredits)} /{" "}
							{formatNumberWithSpaces(props.totalCredits)}
						</span>
					</div>
				</StyledTableNew.TableCell>
			</Show>
		</StyledTableNew.TableRow>
	);
};

// Компонент строки участника
const MemberRow: Component<{
	member: TeamMember;
	isCurrentUser: boolean;
	userRole: MemberRole;
	onRemove: () => void;
	hideCredits?: boolean;
}> = (props) => {
	const queryClient = useQueryClient();
	const totalCredits = () => TeamMethods.getMemberCreditsTotal(props.member);
	const usedCredits = () =>
		totalCredits() - TeamMethods.getMemberCurrentCreditsBalance(props.member);
	const isPending = () => TeamMethods.isMemberPending(props.member);

	const willBeDeletedDate = createMemo(() => {
		if (!props.member.will_be_deleted) return null;
		const date = new Date(props.member.will_be_deleted);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric"
		});
	});

	const cancelInvitationMutation = TeamApi.useCancelInvitationMutation({
		onSuccess: () => {
			showToastMessage({ title: "Invitation cancelled successfully" });
			queryClient.refetchQueries({ queryKey: ["teamInfo"] });
		},
		onError: (error) => {
			showToastMessage({
				title: `Failed to cancel invitation: ${getPublicMessageFromError(error)}`,
				type: ToastMessageTypes.Error
			});
		}
	});

	const handleCopyLink = () => {
		if (props.member.invitation_token) {
			EventAnalytics.team.inviteMember.copyLink(props.member.user_name || "");
			EventAnalytics.team.management.inviteLinkCopied();
			navigator.clipboard.writeText(
				TeamInvitationPageParams.constructTeamInvitationLink({
					token: props.member.invitation_token
				})
			);
			showToastMessage({ title: "Link copied to clipboard" });
		}
	};

	const handleCancelInvite = () => {
		EventAnalytics.team.inviteMember.pendingCancel();
		EventAnalytics.team.management.inviteCancelled();
		if (!props.member.invitation_id) {
			showToastMessage({
				title: "Unable to cancel invitation: invitation ID is missing",
				type: ToastMessageTypes.Error
			});
			return;
		}
		cancelInvitationMutation.mutate({
			invitation_id: String(props.member.invitation_id)
		});
	};

	return (
		<StyledTableNew.TableRow>
			<StyledTableNew.TableCell class="!py-[11px] border border-transparent border-r-0">
				<div class="flex h-8 items-center gap-1.5">
					<div class="flex h-6 w-6 items-center justify-center rounded-md bg-background-interaction-tertiary">
						<span class="caption-12-medium h-fit text-center font-bold text-text-primary uppercase leading-none">
							{getInitials(props.member.user_name)}
						</span>
					</div>
					<span class="caption-12-medium truncate text-text-primary">
						{props.member.user_name}
					</span>
					<Show when={props.isCurrentUser}>
						<span class="caption-12-medium text-text-tertiary">(you)</span>
					</Show>
					<Show when={props.member.will_be_deleted}>
						<StyledTooltip.TooltipClosedComponent
							content={
								<div class="flex max-w-[320px] flex-col gap-1 p-4">
									<span class="caption-12-semibold text-white">
										This seat will be removed
									</span>
									<span class="caption-12-regular text-white">
										This member will be removed from your team on{" "}
										{willBeDeletedDate()}
									</span>
								</div>
							}
							rootProps={{
								positioning: {
									placement: "top"
								}
							}}
						>
							{(buttonProps) => (
								<button {...buttonProps} type="button" class="flex items-center">
									<Info size={16} class="shrink-0 text-text-secondary-v2" />
								</button>
							)}
						</StyledTooltip.TooltipClosedComponent>
					</Show>
				</div>
			</StyledTableNew.TableCell>
			<StyledTableNew.TableCell class="!py-[11px] border border-transparent border-x-0">
				<Show
					when={isPending()}
					fallback={
						<div class="flex h-8 items-center gap-2">
							<span class="caption-12-medium text-text-primary">
								{props.userRole === MemberRole.Owner ? "Owner" : "Member"}
							</span>
							<Show when={!props.isCurrentUser}>
								<Button
									variant="TertiaryNeutral"
									size="small"
									class="invisible opacity-0 group-hover:visible group-hover:opacity-100"
									onClick={() => {
										EventAnalytics.team.deleteMember.removeClick();
										EventAnalytics.team.management.memberRemoveClicked();
										props.onRemove();
									}}
								>
									<Trash size={16} />
									<span>Remove</span>
								</Button>
							</Show>
						</div>
					}
				>
					<div class="flex h-8 items-center gap-1">
						<StatusBadge status={PlanStatus.Pending} />
						{/* Action Buttons */}
						<div class="flex items-center gap-1">
							<Button
								variant="TertiaryNeutral"
								size="small"
								onClick={handleCancelInvite}
								disabled={cancelInvitationMutation.isPending}
							>
								Cancel
							</Button>
							<Button variant="TertiaryNeutral" size="small" onClick={handleCopyLink}>
								<Copy size={12} />
								<span>Copy link</span>
							</Button>
						</div>
					</div>
				</Show>
			</StyledTableNew.TableCell>
			<StyledTableNew.TableCell class="!py-[11px] border border-transparent border-x-0 text-center">
				<div class="flex h-8 items-center justify-center">
					<span class="caption-12-medium text-text-primary">
						{props.member.tariff_name || "-"}
					</span>
				</div>
			</StyledTableNew.TableCell>
			<Show when={!props.hideCredits}>
				<StyledTableNew.TableCell class="!py-[11px] border border-transparent border-l-0 text-right">
					<div class="flex h-8 items-center justify-end">
						<span class="caption-12-medium">
							<span class="text-text-primary">
								{formatNumberWithSpaces(usedCredits())}
							</span>
							<span class="text-text-secondary"> / </span>
							<span class="text-text-secondary">
								{formatNumberWithSpaces(totalCredits())}
							</span>
						</span>
					</div>
				</StyledTableNew.TableCell>
			</Show>
		</StyledTableNew.TableRow>
	);
};

const DeleteMemberModal: Component<{
	isOpen: boolean;
	onClose: () => void;
	memberEmail: string;
	companyAccountId: number;
}> = (props) => {
	const [modalShownSent, setModalShownSent] = createSignal(false);

	createEffect(() => {
		if (props.isOpen && !modalShownSent()) {
			EventAnalytics.team.deleteMember.modalShown(props.memberEmail);
			setModalShownSent(true);
		}

		// Reset flag when modal closes
		if (!props.isOpen && modalShownSent()) {
			setModalShownSent(false);
		}
	});

	const queryClient = useQueryClient();
	const removeMemberMutation = TeamApi.useRemoveMemberMutation({
		onSuccess: () => {
			EventAnalytics.team.deleteMember.success(props.memberEmail);
			showToastMessage({ title: "Member removed successfully" });
			queryClient.refetchQueries({ queryKey: ["teamInfo"] });
			props.onClose();
		},
		onError: (error) => {
			EventAnalytics.team.deleteMember.error({
				member_email: props.memberEmail,
				error_message: getPublicMessageFromError(error) || "Unknown error"
			});
			showToastMessage({
				title: `Failed to remove member: ${getPublicMessageFromError(error)}`,
				type: ToastMessageTypes.Error
			});
		}
	});

	const handleDelete = () => {
		EventAnalytics.team.deleteMember.confirm(props.memberEmail);
		removeMemberMutation.mutate({
			company_account_id: props.companyAccountId
		});
	};

	return (
		<Show when={props.isOpen}>
			<Portal>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					toggleSelf={props.onClose}
					class="flex-col justify-start"
					autoClose
				>
					<ModalV2Ui.Header>
						<ModalV2Ui.HeaderTextContainer>
							<div class="flex items-center gap-2">
								<DeleteUserIcon />
								<h2 class="header-30-semibold text-text-primary">Delete member</h2>
							</div>
							<p class="caption-14-medium text-text-secondary-v2">
								Are you sure you want to delete {props.memberEmail}?
							</p>
						</ModalV2Ui.HeaderTextContainer>
					</ModalV2Ui.Header>
					<ModalV2Ui.ContentContainer class="h-12"></ModalV2Ui.ContentContainer>
					<ModalV2Ui.ButtonContainer>
						<Button
							onClick={() => {
								EventAnalytics.team.deleteMember.cancel(props.memberEmail);
								props.onClose();
							}}
							variant="TertiaryAccent"
							size="large"
							disabled={removeMemberMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							variant="DangerOverlay"
							size="large"
							pending={removeMemberMutation.isPending}
						>
							{removeMemberMutation.isPending ? "Deleting..." : "Delete"}
						</Button>
					</ModalV2Ui.ButtonContainer>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

// Модальное окно для приглашения пользователя
const InviteMemberModal: Component<{
	isOpen: null | Pick<TeamMember, "company_account_id">;
	onClose: () => void;
}> = (props) => {
	const [modalShownSent, setModalShownSent] = createSignal(false);

	createEffect(() => {
		if (props.isOpen && !modalShownSent()) {
			EventAnalytics.team.inviteMember.modalShown();
			setModalShownSent(true);
		}

		// Reset flag when modal closes
		if (!props.isOpen && modalShownSent()) {
			setModalShownSent(false);
		}
	});

	const queryClient = useQueryClient();
	const createInvitationMutation = TeamApi.useCreateInvitationMutation({
		onSuccess: (_, req) => {
			EventAnalytics.team.inviteMember.sentToastShown(req.email);
			EventAnalytics.team.management.inviteSent();
			showToastMessage({ title: `Team invite has been sent to ${req.email}` });
			queryClient.refetchQueries({ queryKey: ["teamInfo"] });
			props.onClose();
			form.reset();
		},
		onError: (error) => {
			EventAnalytics.team.inviteMember.error();
			showToastMessage({
				title: `Failed to send invitation: ${getPublicMessageFromError(error)}`,
				type: ToastMessageTypes.Error
			});
		}
	});

	const form = createForm(() => ({
		defaultValues: {
			email: ""
		},
		onSubmit({ value }) {
			EventAnalytics.team.inviteMember.sendInvite();
			const companyAccountId = props.isOpen?.company_account_id;
			if (!companyAccountId) {
				showToastMessage({
					title: "Unable to get team information",
					type: ToastMessageTypes.Error
				});
				return;
			}
			createInvitationMutation.mutate({
				company_account_id: companyAccountId,
				email: value.email
			});
		}
	}));

	const handleClose = () => {
		EventAnalytics.team.inviteMember.cancel();
		form.reset();
		props.onClose();
	};

	return (
		<Show when={props.isOpen}>
			<Portal>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					toggleSelf={handleClose}
					class="flex-col justify-start"
					autoClose
				>
					<form onSubmit={(e) => e.preventDefault()}>
						<ModalV2Ui.Header toggleSelf={handleClose} showCloseButton>
							<ModalV2Ui.HeaderTextContainer>
								<h2 class="header-30-semibold text-text-primary">
									Invite a Team Member
								</h2>
								<p class="caption-13-medium text-text-secondary-v2">
									Because teamwork makes the dream work ✨
								</p>
							</ModalV2Ui.HeaderTextContainer>
						</ModalV2Ui.Header>
						<ModalV2Ui.ContentContainer class="flex-col items-start gap-2">
							<form.Field
								name="email"
								validators={{
									onSubmit({ value }) {
										if (!value || value.trim() === "") {
											return "Email is required";
										}
										if (!validateEmailString(value)) {
											return "Please enter a valid email address";
										}
									}
								}}
							>
								{(field) => (
									<InputLabelError
										id={field().name}
										labelContent="Email address"
										class="flex-1"
										errorMessage={field().state.meta.errors[0]}
									>
										<Input
											id={field().name}
											type="email"
											variant="Gray"
											size="medium"
											placeholder="Enter new member's email"
											value={field().state.value}
											onChange={(e) =>
												field().handleChange(e.currentTarget.value)
											}
											onBlur={field().handleBlur}
											onInput={() =>
												EventAnalytics.team.inviteMember.emailInput()
											}
										/>
									</InputLabelError>
								)}
							</form.Field>
							<span class="caption-12-medium text-start text-text-secondary">
								Your teammate will get an invite link to join.
							</span>
						</ModalV2Ui.ContentContainer>
						<ModalV2Ui.ButtonContainer>
							<Button
								onClick={handleClose}
								variant="TertiaryAccent"
								size="large"
								disabled={createInvitationMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								onClick={form.handleSubmit}
								variant="PrimaryAccent"
								size="large"
								type="submit"
								disabled={createInvitationMutation.isPending}
							>
								{createInvitationMutation.isPending ? "Sending..." : "Send Invite"}
							</Button>
						</ModalV2Ui.ButtonContainer>
					</form>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

// Компонент формы для добавления мест (только поле seats)
const AddSeatsFormInput: Component = () => {
	const methods = TeamMethods.useTeamSeatsModalContext();

	// Track previous value for increment/decrement detection
	const [previousValue, setPreviousValue] = createSignal<number>(1);

	// Debounce для события countChanged
	let debounceTimer: ReturnType<typeof setTimeout> | undefined;

	// Cleanup debounce timer on component unmount
	onCleanup(() => {
		clearTimeout(debounceTimer);
	});

	const sendCountChangedEvent = (value: string) => {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			const seatsToAdd = Number.parseInt(value, 10) || 1;
			const currentSize = methods.config.currentTeamSize || 0;
			const query = methods.teamPricingQuery();
			const data = query.data;
			const totalDue =
				data && "total_cost" in data ? Number.parseFloat(data.total_cost.toString()) : 0;
			const paymentMethod = methods.getSelectedPaymentMethodWithoutNew();

			EventAnalytics.team.addSeats.countChanged({
				seats_count_current: currentSize,
				seats_count_new: currentSize + seatsToAdd,
				total_due: totalDue,
				payment_method: paymentMethod?.system || "unknown"
			});
			EventAnalytics.team.management.seatsModalAdjusted();
		}, 500);
	};

	return (
		<div class="flex w-full gap-2.5">
			<InputLabel id={"team_name"} labelContent="Team name" class="flex-1 shrink-0">
				<Input
					id={"team_name"}
					variant="Gray"
					placeholder="Enter Team name"
					size="medium"
					class="text-ellipsis"
					value={methods.config.teamName || ""}
					disabled
				/>
			</InputLabel>
			<methods.Form.Field name="seats">
				{(field) => {
					const handleValueChange = (value: string) => {
						const newValue = Number.parseInt(value, 10) || 1;
						const prevValue = previousValue();

						// Send increment/decrement analytics immediately (not debounced)
						if (newValue > prevValue) {
							EventAnalytics.team.addSeats.increaseClick(newValue);
						} else if (newValue < prevValue) {
							EventAnalytics.team.addSeats.decreaseClick(newValue);
						}

						setPreviousValue(newValue);
						field().handleChange(value);
						sendCountChangedEvent(value);
					};

					return (
						<InputLabelError
							id={field().name}
							labelContent="Seats"
							class="w-max"
							errorMessage={field().state.meta.errors[0]}
						>
							<SeatsNumberInput
								value={String(field().state.value || "1")}
								min={1}
								onChange={handleValueChange}
							/>
						</InputLabelError>
					);
				}}
			</methods.Form.Field>
		</div>
	);
};

// Модальное окно для добавления мест
const AddMoreSeatsModal: Component<{
	isOpen: boolean;
	onClose: () => void;
	currentSeats: number;
	teamName?: string;
}> = (props) => {
	const [modalShownSent, setModalShownSent] = createSignal(false);

	createEffect(() => {
		if (props.isOpen && !modalShownSent()) {
			EventAnalytics.team.addSeats.modalShown(props.currentSeats);
			EventAnalytics.team.management.seatsModalOpened();
			setModalShownSent(true);
		}

		// Reset flag when modal closes
		if (!props.isOpen && modalShownSent()) {
			setModalShownSent(false);
		}
	});

	const queryClient = useQueryClient();

	return (
		<Show when={props.isOpen}>
			<Portal>
				<TeamSeatsModal
					config={{
						title: "Add new seats",
						subtitle: "Add more seats to grow your team instantly.",
						minSeats: 1,
						buttonText: "Add seats",
						showBillingPlanSelector: false,
						isAddingSeats: true,
						currentTeamSize: props.currentSeats,
						teamName: props.teamName,
						FormInputs: AddSeatsFormInput,
						getPromoMessage: (state) => {
							const seatsToAdd = Number.parseInt(state.seats(), 10) || 1;
							const totalSeats = props.currentSeats + seatsToAdd;
							return TeamMethods.getPromoMessage(totalSeats);
						},
						showContactSales: (state) => {
							const seatsToAdd = Number.parseInt(state.seats(), 10) || 1;
							const totalSeats = props.currentSeats + seatsToAdd;
							return TeamMethods.shouldShowContactSales(totalSeats);
						},
						onPayment: async (state) => {
							const seatsToAdd = Number.parseInt(state.seats(), 10) || 1;
							const paymentMethod = state.getSelectedPaymentMethodWithoutNew();
							const totalSeats = props.currentSeats + seatsToAdd;
							const query = state.teamPricingQuery();
							const data = query.data;
							const totalDue =
								data && "total_cost" in data
									? Number.parseFloat(data.total_cost.toString())
									: 0;

							EventAnalytics.team.addSeats.confirm({
								seats_count_current: props.currentSeats,
								seats_count_new: totalSeats,
								total_due: totalDue,
								payment_method: paymentMethod?.system || "unknown"
							});
							EventAnalytics.team.management.seatsModalConfirmed();

							try {
								const request: AddMembersRequest = {
									new_seats_subscriptions: [
										{
											plan_name: "teams",
											quantity: seatsToAdd
										}
									],
									payment_method_id: paymentMethod?.id || null
								};

								const response = await TeamApi.addMembers(request);

								// If there's a redirect URL (for payment), redirect user
								if (response.redirect_url) {
									window.location.href = response.redirect_url;
									return;
								}

								// Success - refresh team data and close modal
								await queryClient.refetchQueries({ queryKey: ["teamInfo"] });
								EventAnalytics.team.addSeats.success({
									seats_added: seatsToAdd,
									new_total_seats: totalSeats
								});
								showToastMessage({
									title: `Successfully added ${seatsToAdd} seat${seatsToAdd > 1 ? "s" : ""}`,
									type: ToastMessageTypes.Success
								});
								props.onClose();
							} catch (error) {
								EventAnalytics.team.addSeats.error({
									seats_count_current: props.currentSeats,
									seats_count_new: totalSeats,
									error_message:
										getPublicMessageFromError(error) || "Unknown error"
								});
								showToastMessage({
									title: `Failed to add seats: ${getPublicMessageFromError(error)}`,
									type: ToastMessageTypes.Error
								});
							}
						},
						onClose: () => {
							EventAnalytics.team.addSeats.cancel();
							props.onClose();
						}
					}}
				/>
			</Portal>
		</Show>
	);
};

// Главный компонент таблицы участников команды
export const TeamMembersTable: Component = () => {
	const teamDataOptions = createMemo(() => TeamApi.getTeamInfoOptions());
	const teamData = useQuery(teamDataOptions);

	const [isInviteModalOpen, setIsInviteModalOpen] = createSignal<null | Pick<
		TeamMember,
		"company_account_id"
	>>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = createSignal(false);
	const [isAddSeatsModalOpen, setIsAddSeatsModalOpen] = createSignal(false);
	const [memberToDelete, setMemberToDelete] = createSignal<TeamMember | null>(null);
	const [isExpanded, setIsExpanded] = createSignal(false);
	const INITIAL_VISIBLE_COUNT = 5;

	const members = () => teamData.data?.members || [];
	const isOldPlan = () => teamData.data?.plan_status === "old";

	const visibleMembers = createMemo(() => {
		const allMembers = members();
		if (isExpanded() || allMembers.length <= INITIAL_VISIBLE_COUNT) {
			return allMembers;
		}
		return allMembers.slice(0, INITIAL_VISIBLE_COUNT);
	});

	const shouldShowExpandButton = createMemo(() => {
		return members().length > INITIAL_VISIBLE_COUNT;
	});

	const isButtonsDisabled = () => {
		const status = teamData.data?.team_subscription_status;
		const hasCancelAt = !!teamData.data?.cancel_at;
		return status !== "active" || hasCancelAt;
	};

	const seatsInfo = createMemo(() => ({
		current: teamData.data?.team_member_total_amount || 0,
		active: teamData.data?.members?.filter((m) => m.user_id !== null).length || 0
	}));

	const handleRemoveMember = (member: TeamMember) => {
		setMemberToDelete(member);
		setIsDeleteModalOpen(true);
	};

	return (
		<>
			<div class="w-full rounded-[40px] bg-white p-10">
				<div class="mb-8 flex items-center justify-between">
					<h1 class="heading-24-semibold text-text-primary">Members</h1>
					<div class="flex items-center gap-4">
						<Button
							variant="FlexButtonSecondary"
							size="small"
							onClick={() => {
								EventAnalytics.team.seatsButtons.removeSeatsClick({
									current_seats: seatsInfo().current,
									active_members: seatsInfo().active
								});
								if (isOldPlan()) {
									openContactUsForm();
								} else {
									TeamMethods.openUpdateSeatsModal();
								}
							}}
							disabled={!isOldPlan() && isButtonsDisabled()}
						>
							Remove seats
						</Button>
						<Button
							variant="TertiaryOverlay"
							size="small"
							onClick={() => {
								EventAnalytics.team.seatsButtons.addSeatsClick({
									current_seats: seatsInfo().current,
									active_members: seatsInfo().active
								});
								EventAnalytics.team.management.addMoreSeatsClicked();
								if (isOldPlan()) {
									openContactUsForm();
									EventAnalytics.team.management.contactSalesClicked();
								} else {
									setIsAddSeatsModalOpen(true);
								}
							}}
							disabled={!isOldPlan() && isButtonsDisabled()}
						>
							Add more seats
						</Button>
					</div>
				</div>

				<div class="flex flex-col gap-6">
					<StyledTableNew.Table>
						<StyledTableNew.TableHead>
							<StyledTableNew.TableRow>
								<StyledTableNew.TableHeaderCell class="min-w-[264px]">
									Email
								</StyledTableNew.TableHeaderCell>
								<StyledTableNew.TableHeaderCell class="min-w-[264px]">
									Role
								</StyledTableNew.TableHeaderCell>
								<StyledTableNew.TableHeaderCell class="min-w-[82px] text-center">
									Plan
								</StyledTableNew.TableHeaderCell>
								<Show when={!isOldPlan()}>
									<StyledTableNew.TableHeaderCell class="min-w-[156px] text-right">
										credits Used / total
									</StyledTableNew.TableHeaderCell>
								</Show>
							</StyledTableNew.TableRow>
						</StyledTableNew.TableHead>
						<StyledTableNew.TableBody>
							<For each={visibleMembers()}>
								{(member) => {
									if (TeamMethods.isBlankMember(member)) {
										const totalCredits =
											TeamMethods.getMemberCreditsTotal(member);
										const currentBalance =
											TeamMethods.getMemberCurrentCreditsBalance(member);
										const usedCredits = totalCredits - currentBalance;
										return (
											<EmptyMemberSlot
												usedCredits={usedCredits}
												totalCredits={totalCredits}
												willBeDeleted={member.will_be_deleted}
												hideCredits={isOldPlan()}
												onClick={() =>
													setIsInviteModalOpen({
														company_account_id:
															member.company_account_id
													})
												}
											/>
										);
									}
									return (
										<MemberRow
											member={member}
											isCurrentUser={
												TeamMethods.getMemberId(member) ===
												getUserStore()?.id
											}
											userRole={
												TeamMethods.getTeamMemberRole(member) ||
												MemberRole.Member
											}
											hideCredits={isOldPlan()}
											onRemove={() => handleRemoveMember(member)}
										/>
									);
								}}
							</For>
						</StyledTableNew.TableBody>
					</StyledTableNew.Table>

					<Show when={shouldShowExpandButton()}>
						<Button
							variant="TertiaryOverlay"
							size="large"
							class="w-fit"
							onClick={() => setIsExpanded(!isExpanded())}
						>
							{isExpanded() ? "See less" : "See more"}
						</Button>
					</Show>
				</div>
			</div>

			<InviteMemberModal
				isOpen={isInviteModalOpen()}
				onClose={() => setIsInviteModalOpen(null)}
			/>

			<DeleteMemberModal
				isOpen={isDeleteModalOpen()}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setMemberToDelete(null);
				}}
				memberEmail={memberToDelete()?.user_name || ""}
				companyAccountId={memberToDelete()?.company_account_id || 0}
			/>

			<AddMoreSeatsModal
				isOpen={isAddSeatsModalOpen()}
				onClose={() => setIsAddSeatsModalOpen(false)}
				currentSeats={TeamMethods.getTeamMemberTotalAmount(teamData.data || null) || 1}
				teamName={teamData.data?.team_name}
			/>
		</>
	);
};
